import React, { useEffect, useState, useRef, useMemo, useContext } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { MCard, MGrid, MSpinner } from "components/Shared";
import { CustomerInsightNotificationAccountAddRequest, CustomerInsightNotificationAccountListResponse, CustomerInsightNotificationResponse, CustomerListResponse, MajorCompanyDto } from "interfaces/Mueller.Business.Models.ts";
import { 
  getCustomerInsightNotification, 
  getCustomerInsightNotificationAccounts, 
  updateCustomerInsightNotification,
  addCustomerInsightNotificationAccount,
  deleteCustomerInsightNotificationAccount,
} from "requests/customerInsights";
import { 
  getCustomerList,
  getMajorCompanyList,
} from "requests/customer";
import responseHandler from "responseHandler/index.js";
import { userHasRight } from "selectors/rights";
import { DialogContext } from "components/Shared/DialogProvider.tsx";
import { CUSTOMER_INSIGHT_NOTIFICATION_VIEW, CUSTOMER_INSIGHT_NOTIFICATION_EDIT } from "typeConstants/rights";
import { DATA_TYPE_DATE, DATA_TYPE_STRING } from "typeConstants/dataTypes.js";
import { MCardControlMethods } from "components/Shared/MCard.tsx";
import { ACTION_ADD, MGridColumnDefinition } from "components/Shared/MGrid.tsx";
import { ACTION_DELETE } from "components/Shared/MDialog.tsx";
import * as yup from "yup";
import AddCustomerInsightNotificationAccount from "./AddCustomerInsightNotificationAccount.tsx";


interface CustomerInsightNotificationProps {
  verticalDisplay: boolean;
  selectedNotification: number;
  refreshParentList: () => void;
  // Redux
  errorToast: (response: Response) => void,
  canViewCustomerDetails: boolean;
  canEditCustomerDetails: boolean;
}

const  CustomerInsightNotification = (props: CustomerInsightNotificationProps) => {
  const [customerInsightNotification, setCustomerInsightNotification] = useState<CustomerInsightNotificationResponse>();
  const [customerInsightNotificationAccounts, setCustomerInsightNotificationAccounts] = useState<CustomerInsightNotificationAccountListResponse>();
  const [fetchingCustomerInsightNotification, setFetchingCustomerInsightNotification] = useState<boolean>(true);
  const [fetchingCustomerInsightNotificationAccounts, setFetchingCustomerInsightNotificationAccounts] = useState<boolean>(true);
  const [selectedCompanyRowIndex, setSelectedCompanyRowIndex] = useState<number | null>(null);
  const [selectedAccountRowIndex, setSelectedAccountRowIndex] = useState<number | null>(null);
  const [availableCustomerAccounts, setAvailableCustomerAccounts] = useState<CustomerListResponse[]>([]);
  const [availableMajorCompanies, setAvailableMajorCompanies] = useState<MajorCompanyDto[]>([]);
  const dialog = useContext(DialogContext);
  const cardControlMethods = useRef<MCardControlMethods>();
  const mounted = useRef(true);

  useEffect(() => {
    if (props.selectedNotification) {
      loadCustomerInsightNotification();
      loadAvailableAccountsAndCompanies();
    }
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if(props.selectedNotification){
      reloadCustomerInsightNotification();
    }
  }, [props.selectedNotification]);

  const loadCustomerInsightNotification = () => { 
      getCustomerInsightNotification(props.selectedNotification, (resp) => {
        setCustomerInsightNotification(resp);
        setFetchingCustomerInsightNotification(false);      
      },(resp) => {    
        props.errorToast(resp);
        setFetchingCustomerInsightNotification(false);      
      }, mounted);     
      getCustomerInsightNotificationAccounts(props.selectedNotification, (accounts) => {
        setCustomerInsightNotificationAccounts(accounts);
        setFetchingCustomerInsightNotificationAccounts(false);
      },(resp) => {    
        props.errorToast(resp);
        setFetchingCustomerInsightNotificationAccounts(false);
      }, mounted);          
  };

  const reloadCustomerInsightNotification = () => {
    setFetchingCustomerInsightNotification(true);
    setFetchingCustomerInsightNotificationAccounts(true);
    loadCustomerInsightNotification();    
  };

  const loadAvailableAccountsAndCompanies = () => {
    getCustomerList((customers) => {
      setAvailableCustomerAccounts(customers);
    }, mounted);

    getMajorCompanyList((companies) => {
      setAvailableMajorCompanies(companies);
    }, mounted);  
  };

  const onAccountCompanyAddSubmit = (request: CustomerInsightNotificationAccountAddRequest) => {
      addCustomerInsightNotificationAccount(
      props.selectedNotification,
      request,
      () => { 
        dialog.hide(); 
        reloadCustomerInsightNotification();
        props.refreshParentList();
      },
      () => dialog.hide(),
      mounted
    );
  };

  const onCompanyAdd = () => {
    const dialogProps = { className: "add-customer-insight-notification-account-dialog" }; 
    let companyArray = availableMajorCompanies?.map(r => ({
      label: r.companyName, value: r.id 
      }));    
    dialog.show(
      "Add Customer Insight Notification Company",
      <AddCustomerInsightNotificationAccount 
        onCancel={() => dialog.hide()} 
        onSubmit={(accountId) => {
          onAccountCompanyAddSubmit({ 
            customerInsightNotificationId: props.selectedNotification, 
            majorCompanyId: accountId,
            customerId: null 
          });
        }}
        availableAccounts={companyArray} 
        cardLabel="Company"        
        customerInsightNotificationId={props.selectedNotification} 
      />,
      null,
      dialogProps);
  };

  const onAccountAdd = () => {
    const dialogProps = { className: "add-customer-insight-notification-account-dialog" };
    let accountArray = availableCustomerAccounts?.map(r => ({
      label: `${r.number} - ${r.name}`, value: r.id 
      }));       
    dialog.show(
      "Add Customer Insight Notification Account",
      <AddCustomerInsightNotificationAccount 
        onCancel={() => dialog.hide()} 
        onSubmit={(accountId) => {
          onAccountCompanyAddSubmit({ 
            customerInsightNotificationId: props.selectedNotification, 
            majorCompanyId: null,
            customerId: accountId 
          });
        }}
        availableAccounts={accountArray} 
        cardLabel="Account"
        customerInsightNotificationId={props.selectedNotification}         
      />,
      null,
      dialogProps);
  };  

  const onInsightAccountDeleteSubmit = (id: number) => {
    deleteCustomerInsightNotificationAccount(
      id,
      () => { 
        dialog.hide(); 
        setSelectedAccountRowIndex(null);
        setSelectedCompanyRowIndex(null);        
        reloadCustomerInsightNotification();
        props.refreshParentList();        
      },
      () => dialog.hide(),
      mounted
    );
  };

  const onInsightAccountDelete = (id: number, type: string) => {
    dialog.showYesNo(`Delete Notification ${type}`, "Are you sure?", () => onInsightAccountDeleteSubmit(id));
  };  

  const companyColumns = useMemo(() => {
    const columns: MGridColumnDefinition[] = [
      { key: "majorCompanyName", name: "Company Name", dataSource: "majorCompany" }
    ];  
    return columns;
  }, [props.canEditCustomerDetails]);

  let companyActions = [];
  if (props.canEditCustomerDetails) {
    companyActions.push(
      { code: ACTION_ADD, onClick: onCompanyAdd },
    ); 
    if (selectedCompanyRowIndex != null) {
      let id = customerInsightNotificationAccounts?.majorCompanyDto[selectedCompanyRowIndex]?.customerInsightNotificationAccountId;
      if(id) {
        companyActions.push({ code: ACTION_DELETE, onClick: () => onInsightAccountDelete(id, "Company") },);
      }
    }        
  }  

  const accountColumns = useMemo(() => {
    const columns: MGridColumnDefinition[] = [
      { key: "customerNumber", name: "Account #", dataSource: "customerNumber" },
      { key: "customerName", name: "Customer Name", dataSource: "customerName" },
    ];
    return columns;
  }, [props.canEditCustomerDetails]);

  let accountActions = [];
  if (props.canEditCustomerDetails) {
    accountActions.push(
      { code: ACTION_ADD, onClick: onAccountAdd},
    ); 
    if (selectedAccountRowIndex != null) {
      let id = customerInsightNotificationAccounts?.customer[selectedAccountRowIndex]?.customerInsightNotificationAccountId;
      if(id) {
        accountActions.push({ code: ACTION_DELETE, onClick: () => onInsightAccountDelete(id, "Account") },);
      }
    }    
  }    

  const memoizedFields = useMemo(
    () => {
      return [
        {
          key: "name",
          label: "Name",
          value: customerInsightNotification?.name,
          editable: props.canEditCustomerDetails,
          dataType: DATA_TYPE_STRING,
          validation: yup.string().required()
        },
        {
          key: "email",
          label: "Email",
          value: customerInsightNotification?.email,
          editable: props.canEditCustomerDetails,
          dataType: DATA_TYPE_STRING,
          validation: yup.string().required()
        },
        {
          key: "startDate",
          label: "Start Date",
          value: customerInsightNotification?.startDate,          
          editable: props.canEditCustomerDetails,
          dataType: DATA_TYPE_DATE,
          validation: yup.date().typeError("Start Date is required").required("Start Date is a required field")
        },
        {
          key: "disableDate",
          label: "Disable Date",
          value: customerInsightNotification?.disableDate,
          editable: props.canEditCustomerDetails,
          dataType: DATA_TYPE_DATE
        },     
      ];
    },
    [customerInsightNotification]
  );


  const onEditFinished = (fieldsChanged) => {
    if (fieldsChanged.length) {
      const isValid = cardControlMethods.current.runValidation();     
      if (isValid) { 
        let updated = { ...cardControlMethods.current.getEditedDataObject(true) };
        updateCustomerInsightNotification(props.selectedNotification, updated, () => {
          loadCustomerInsightNotification();
          props.refreshParentList();
        }, (resp) => {    
            props.errorToast(resp);
          }, mounted);
      }
    }
  };

  if (!props.selectedNotification || fetchingCustomerInsightNotification) {
    return (<MSpinner />);
  }      

  return (
    <>
      <MCard
        title="Customer Insight User Details"
        fields={memoizedFields}
        isEditable={props.canEditCustomerDetails}
        onEditFinished={onEditFinished}
        getControlMethods={cm => cardControlMethods.current = cm}
      />
      <div className="customer-insight-notification-grid-container md-paper--1 md-cell--12">
      <MGrid
        headerText="Authorized Companies"
        columnDefinitions={companyColumns}
        data={customerInsightNotificationAccounts?.majorCompanyDto.map(s => ({
          id: s.customerInsightNotificationAccountId,
          majorCompany: s.companyName
        }))}
        isFetchingData={fetchingCustomerInsightNotificationAccounts}
        onRowClick={null}
        allowRowSelection={true}
        onRowSelectionChanged={indexes => setSelectedCompanyRowIndex(indexes.length > 0 ? indexes.at(-1) : null)}
        selectedRows={selectedCompanyRowIndex !== null ? [selectedCompanyRowIndex] : []}
        displayNumOfItemsFound={true}
        actions={companyActions}
        cardStyle
      />           
      </div>        
      <div className="customer-insight-notification-grid-container md-paper--1 md-cell--12">
        <MGrid  
          headerText="Authorized Accounts"
          columnDefinitions={accountColumns}        
          data={customerInsightNotificationAccounts?.customer.map(s => {
            return {
              id: s.customerInsightNotificationAccountId,
              customerNumber: s.customerNumber,
              customerName: s.customerName
            };
          })}
          isFetchingData={fetchingCustomerInsightNotificationAccounts}
          onRowClick={null}
          allowRowSelection={true}
          onRowSelectionChanged={indexes => setSelectedAccountRowIndex(indexes.length > 0 ? indexes.at(-1) : null)}
          selectedRows={selectedAccountRowIndex !== null ? [selectedAccountRowIndex] : []}
          displayNumOfItemsFound={true}
          actions={accountActions}
          cardStyle       
        />    
      </div>       
    </>
  );
};

const mapStateToProps = state => ({
  canViewCustomerDetails: userHasRight(CUSTOMER_INSIGHT_NOTIFICATION_VIEW)(state),
  canEditCustomerDetails: userHasRight(CUSTOMER_INSIGHT_NOTIFICATION_EDIT)(state),
});

const mapDispatchToProps = (dispatch) => ({
  errorToast(response: Response) {
    responseHandler.error(response, dispatch, false);
  },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CustomerInsightNotification));